import { useState, useEffect } from 'react';
import { Card, Form, Tab } from 'react-bootstrap';

import {
    faCheck,
    faChevronLeft,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import apiClient, { handleAPIError } from 'helpers/apiService';
import Button from 'components/base/Button';
import classNames from 'classnames';
import Badge from 'components/base/Badge';
import { Question, QuestionarieData } from './DataDefinitions';



const Questionarie = ({
    assessment_type,
    onSubmit,
}: { assessment_type: string, onSubmit?: (questionarie: any) => void }) => {
    const { t } = useTranslation();

    const [step, setStep] = useState(1);
    const [questionarieLength, setQuestionarieLength] = useState(99);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);

    const [questionarie, setQuestionarie] = useState<QuestionarieData>({
        role: '',
        assessment: []
    });

    const fetchAvailableRoles = async () => {
        try {
            const response = await apiClient.get('/v1/skills/templates/' + assessment_type);
            setRoles(response.data);
            setQuestionarie({ ...questionarie, ['role']: '' })
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchQuestionarieTemplate = async (role: string) => {
        setLoading(true);
        try {
            const { data } = await apiClient.get(`/v1/skills/templates/${assessment_type}/${role}`);

            setQuestionarie({
                ...questionarie, ['role']: data.role,
                //['assessment']: response.data.questions 
                assessment: data.questions.map((question: Question) => ({
                    ...question,
                    evaluation: 0, // Set to 0 as default
                }))

            })
            setQuestionarieLength(data.questions.length);

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (onSubmit)
            onSubmit(questionarie);
    };


    useEffect(() => {

        if (roles.length == 0) {
            fetchAvailableRoles()
        }

        if (step == 2 && questionarie.role !== '')
            fetchQuestionarieTemplate(questionarie.role);
    }, [step]);


    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <Tab.Container activeKey={step}
                onSelect={(eventKey: string | null) => {
                    if (eventKey) {
                        setStep(parseInt(eventKey));
                    }
                }}>
                <Card className="theme-wizard">
                    <Card.Header className="bg-100 pt-3 pb-2 border-bottom-0">
                        {/*<WizardNav currentStep={step} /> */}
                    </Card.Header>
                    <Card.Body className="pb-0">

                        <Tab.Content>
                            <Tab.Pane eventKey={1}>
                                <div className="mt-2 mb-2" style={{ minHeight: '100px' }}>
                                    <h4 className="mb-2 text-info">{t("Questionarie.pick_a_role")}</h4>

                                    <Form.Select aria-label="pick a role to start the skills assessment" value={questionarie?.role} onChange={(e) => setQuestionarie({ ...questionarie, ['role']: e.target.value })}>
                                        <option>{t("Questionarie.pick_role")}</option>
                                        {roles.map((role, index) => (
                                            <option key={index} value={role}>{role}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                            </Tab.Pane>


                            {questionarie.assessment.map((question, index) => (
                                <Tab.Pane key={index} eventKey={index + 2} >
                                    <div className="mt-2 mb-2" style={{ minHeight: '300px' }}>
                                        <h3 className="mb-2">{t("Questionarie.question")}: {index + 1} - {questionarie.assessment.length}</h3>

                                        <Card
                                            bg="info"
                                            text='white'
                                        >
                                            <Card.Body>

                                                <Card.Text>
                                                    Please rate your ability to express each of the required competences on a scale from 1 to 100,
                                                    where 1 indicates extremely low ability and 100 indicates the maximum ability.
                                                </Card.Text>
                                            </Card.Body>
                                        </Card>

                                        <Card className={'border border-Light mt-4 mb-4'}>
                                            <Card.Body>
                                                <Card.Title as="h4">{question.competence} ({question.evaluation ? question.evaluation : 1})</Card.Title>
                                                <Card.Text>{question.description} </Card.Text>
                                            </Card.Body>
                                        </Card>

                                        <div className="d-flex gap-1">
                                            <Badge bg="primary" style={{ minWidth: '50px' }}> 1 </Badge>
                                            <Form.Range value={question.evaluation ? question.evaluation : 1} onChange={(e) =>

                                                setQuestionarie({
                                                    ...questionarie,
                                                    assessment: questionarie.assessment.map((r, i) => (i === index ? { ...r, evaluation: parseInt(e.target.value) } : r))
                                                })
                                            }

                                                min='1'
                                                max='100'
                                            />
                                            <Badge bg="primary" style={{ minWidth: '50px' }}> 100 </Badge>
                                        </div>
                                    </div>
                                </Tab.Pane>
                            ))}



                            <Tab.Pane eventKey={questionarie.assessment.length + 2}>
                                <div className="mt-2 mb-2 align-middle" style={{ minHeight: '100px', textAlign: 'center' }}>
                                    <div className="text-center d-inline-block text-success">
                                        <FontAwesomeIcon icon={faCheck} className='fs-5' />
                                        <span className="d-none d-md-block mt-1 fs-5">{t("Questionarie.done")}</span>
                                    </div>

                                </div>

                            </Tab.Pane>
                        </Tab.Content>

                    </Card.Body>
                    <Card.Footer className="border-top-0">

                        <div
                            className='d-flex justify-content-between mb-0'
                        >
                            <Button
                                variant="link"
                                className={classNames('p-0', {
                                    'd-none': step <= 1
                                })}
                                startIcon={<FontAwesomeIcon icon={faChevronLeft} className="fs-10" />}

                                onClick={() => setStep(step > 1 ? step - 1 : step)}

                            >
                                {t("Questionarie.Previous")}
                            </Button>
                            <Button
                                variant="primary"
                                className={classNames('ms-auto px-6', {
                                    'd-none': step > questionarieLength + 1
                                })}
                                disabled={questionarie.role === ''}
                                endIcon={<FontAwesomeIcon icon={faChevronRight} className="fs-10" />}
                                onClick={() => setStep(step <= questionarieLength + 1 ? step + 1 : step)}
                            >
                                {step === 1 ? t("Questionarie.Start") : t("Questionarie.Next")}
                            </Button>

                            <Button
                                variant="primary"
                                className={classNames('ms-auto px-6', {
                                    'd-none': step <= questionarieLength + 1
                                })}
                                onClick={handleSubmit}
                            >
                                {t("Questionarie.Submit")}
                            </Button>
                        </div>


                    </Card.Footer>
                </Card>
            </Tab.Container>


        </div >
    );


};

export default Questionarie;
